import { MIN_BYLINE_MENU_LINKS } from '../utils/selectBylineMenuLinks.ts';

import type { IncomingLinksSsrState } from './incomingLinksSsrState.ts';
import type { OutboundBylineSsrState } from './outboundBylineSsrState.ts';
import type { RelatedContentSsrState } from './relatedContentSsrState.ts';

export type BylineSsrPlaceholderInput = {
	outboundState: OutboundBylineSsrState | undefined;
	incomingLinksState: IncomingLinksSsrState | undefined;
	relatedContentState: RelatedContentSsrState | undefined;
	hasDevDocsConnection: boolean;
};

const countSampledRows = (
	state: IncomingLinksSsrState | RelatedContentSsrState | undefined,
): number => (state?.status === 'present' ? state.count : 0);

/**
 * Decides, from the three preloaded SSR samples, whether the server paints the byline placeholder.
 * Mirrors the client's `getBylineVisibility` for the default menu: the byline shows once Links to,
 * Appears in and Related content together reach the row minimum, or the page has a Dev Docs
 * connection. Each sample is capped at three rows, so the sum proves "at least three" and no more,
 * and it is an upper bound: the client can still drop rows ORS cannot resolve. A sample that is
 * missing, empty or unavailable contributes nothing. CCINT-9359
 *
 * The footprint itself is fixed at three tiles (CCINT-9354), so this only answers paint or not.
 */
export const shouldPaintBylineSsrPlaceholder = ({
	outboundState,
	incomingLinksState,
	relatedContentState,
	hasDevDocsConnection,
}: BylineSsrPlaceholderInput): boolean => {
	if (hasDevDocsConnection) {
		return true;
	}
	const outboundLinkCount = outboundState?.status === 'present' ? outboundState.linkCount : 0;
	const sampledLinkCount =
		outboundLinkCount +
		countSampledRows(incomingLinksState) +
		countSampledRows(relatedContentState);
	return sampledLinkCount >= MIN_BYLINE_MENU_LINKS;
};
